import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { duration, ease } from '../../../theme/tokens';

type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'fade' | 'zoom';

interface RevealProps {
  children: ReactNode;
  /** Direction the element travels from. */
  from?: RevealDirection;
  /** Stagger, in milliseconds. */
  delay?: number;
  /** How much of the element must be visible before it plays (0 - 1). */
  threshold?: number;
  /** Replay every time it scrolls back into view. */
  repeat?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const offset: Record<RevealDirection, string> = {
  up: 'translate3d(0, 34px, 0)',
  down: 'translate3d(0, -34px, 0)',
  left: 'translate3d(-40px, 0, 0)',
  right: 'translate3d(40px, 0, 0)',
  fade: 'none',
  zoom: 'scale(0.94)',
};

const Wrapper = styled.div<{ $visible: boolean; $from: RevealDirection; $delay: number }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible, $from }) => ($visible ? 'none' : offset[$from])};
  transition:
    opacity ${duration.reveal} ${ease.out},
    transform ${duration.reveal} ${ease.out};
  transition-delay: ${({ $delay }) => $delay}ms;
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

/**
 * Scroll-triggered entrance. Wrap any block to have it drift in once it
 * enters the viewport — used for section headers, cards and form panels.
 */
const Reveal = ({
  children,
  from = 'up',
  delay = 0,
  threshold = 0.15,
  repeat = false,
  className,
  style,
}: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (!repeat) observer.unobserve(entry.target);
        } else if (repeat) {
          setVisible(false);
        }
      },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, repeat]);

  return (
    <Wrapper ref={ref} $visible={visible} $from={from} $delay={delay} className={className} style={style}>
      {children}
    </Wrapper>
  );
};

export default Reveal;
